import { Link } from "wouter";
import { Stethoscope } from "lucide-react";
import { SEO } from "@/components/SEO";

function LegalShell({
  title,
  canonical,
  children,
}: {
  title: string;
  canonical: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background font-sans">
      <SEO
        title={`${title} — LRScribe`}
        description={`${title} for LRScribe, the HIPAA-compliant AI medical transcription platform.`}
        canonical={canonical}
      />
      <header className="border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link href="/" className="flex items-center space-x-2 group" data-testid="link-home">
            <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
              <Stethoscope className="w-6 h-6 text-primary" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">
              LRScribe
            </span>
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-8 inline-block rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-3 py-1 text-xs font-semibold">
          Placeholder content — replace before launch
        </div>
        <h1 className="text-4xl font-heading font-extrabold text-foreground mb-6">
          {title}
        </h1>
        <div className="prose prose-slate dark:prose-invert max-w-none text-muted-foreground space-y-4">
          {children}
        </div>
        <div className="mt-12">
          <Link href="/" className="text-primary hover:underline" data-testid="link-back-home">
            ← Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" canonical="https://lrscribe.com/privacy">
      <p>
        This is a placeholder Privacy Policy for LRScribe. The production
        version will describe how LRScribe collects, stores, and processes
        protected health information (PHI), audio recordings, account data,
        and usage information in accordance with HIPAA and applicable
        regulations.
      </p>
      <p>
        Topics to be covered include data collection, the purpose of
        processing, retention and deletion, sub-processors, encryption in
        transit and at rest, user rights, and how to contact us with
        privacy-related requests.
      </p>
      <p>
        For questions before launch, please reach out via the{" "}
        <Link href="/contact" className="text-primary hover:underline">
          contact page
        </Link>
        .
      </p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" canonical="https://lrscribe.com/terms">
      <p>
        This is a placeholder Terms of Service for LRScribe. The production
        version will define the agreement between LRScribe and its users,
        including acceptable use, account responsibilities, intellectual
        property, billing terms, and limitations of liability.
      </p>
      <p>
        LRScribe is designed to assist licensed healthcare professionals
        with clinical documentation. It does not provide medical advice and
        does not replace professional clinical judgment.
      </p>
      <p>
        These terms will be finalized prior to general availability. For
        questions, please use the{" "}
        <Link href="/contact" className="text-primary hover:underline">
          contact page
        </Link>
        .
      </p>
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell title="Contact" canonical="https://lrscribe.com/contact">
      <p>
        We'd love to hear from clinicians, practice administrators, and
        compliance officers interested in LRScribe.
      </p>
      <p>
        Email:{" "}
        <a
          href="mailto:hello@lrscribe.com"
          className="text-primary hover:underline"
          data-testid="link-email"
        >
          hello@lrscribe.com
        </a>
      </p>
      <p>
        For sales, partnership, or HIPAA / BAA questions, include your role
        and organization in your message and we'll route it to the right
        team.
      </p>
    </LegalShell>
  );
}
